// לוח מאפיינים: גאומטריה, יישור לבאנר, טקסט, תמונה, כפתור/צורה ורקע. הכל עריכה ידנית — בלי AI.
import { memo, useEffect, useRef, useState } from 'react';
import type { BannerDoc, BrandKit, Layer } from '@bb/schema';
import { updateDoc, updateLayer } from './docUtils';
import type { AssetMap } from './types';
import { ROLE_LABEL, TYPE_LABEL } from './types';

export interface LibAsset { id: string; kind: string; name: string; mime: string; url: string; width?: number | null; height?: number | null; meta?: any }

interface Props {
  doc: BannerDoc;
  layer: Layer | null;
  kit: BrandKit;
  assets: AssetMap;
  library: LibAsset[];
  campaignCopy: any;
  onChange: (doc: BannerDoc, key?: string) => void;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function Num({ label, value, onChange, step = 1, min, max, suffix }: { label: string; value: number; onChange: (n: number) => void; step?: number; min?: number; max?: number; suffix?: string }) {
  const [s, setS] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setS(String(value)); }, [value]);
  return (
    <label className="pf">
      <span>{label}{suffix && <span className="muted"> ({suffix})</span>}</span>
      <input type="number" className="mono" value={s} step={step} min={min} max={max}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; setS(String(value)); }}
        onChange={(e) => {
          setS(e.target.value);
          let n = parseFloat(e.target.value);
          if (!Number.isFinite(n)) return;
          if (min !== undefined) n = Math.max(min, n);
          if (max !== undefined) n = Math.min(max, n);
          onChange(n);
        }} />
    </label>
  );
}

export function ColorField({ label, value, kit, onChange }: { label: string; value: string; kit: BrandKit; onChange: (hex: string) => void }) {
  const [s, setS] = useState(value);
  useEffect(() => setS(value), [value]);
  const v6 = value.slice(0, 7);
  return (
    <div className="pf">
      <span>{label}</span>
      <div className="row" style={{ gap: 4 }}>
        {kit.colors.map((c, i) => (
          <button key={i} type="button" className={`swatch-btn ${v6.toLowerCase() === c.hex.toLowerCase() ? 'on' : ''}`} style={{ background: c.hex }} title={`${c.name ?? c.role} ${c.hex}`} aria-label={`צבע ${c.name ?? c.role} ${c.hex}`} aria-pressed={v6.toLowerCase() === c.hex.toLowerCase()} onClick={() => onChange(c.hex.toUpperCase())} />
        ))}
        <input type="color" aria-label={`${label} — צבע מותאם`} value={HEX.test(v6) ? v6 : '#000000'} onChange={(e) => onChange(e.target.value.toUpperCase())} />
        <input className="mono" style={{ width: 82 }} aria-label={`${label} — HEX`} value={s} maxLength={7} onChange={(e) => { setS(e.target.value); if (HEX.test(e.target.value)) onChange(e.target.value.toUpperCase()); }} />
      </div>
      {HEX.test(v6) && !kit.colors.some((c) => c.hex.toLowerCase() === v6.toLowerCase()) && <span className="small" style={{ color: 'var(--warn)' }}>צבע מחוץ לפלטת המותג</span>}
    </div>
  );
}

function Seg<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="pf">
      <span id={`seg-${label}`}>{label}</span>
      <div className="seg" role="group" aria-labelledby={`seg-${label}`}>
        {options.map(([v, t]) => <button key={v} type="button" className={value === v ? 'on' : ''} aria-pressed={value === v} onClick={() => onChange(v)}>{t}</button>)}
      </div>
    </div>
  );
}

function approvedCopy(copy: any, key: string | null): string | null {
  if (!key || !copy) return null;
  if (key === 'disclaimer') return copy.disclaimer?.text ?? null;
  for (const arr of [copy.headlines, copy.texts, copy.ctas]) {
    const f = (arr ?? []).find((x: any) => x.key === key);
    if (f) return f.text;
  }
  return null;
}

/** מסיר מ-assetIds פונטים שאף שכבה לא משתמשת במשפחה שלהם */
function pruneFonts(d: BannerDoc, assets: AssetMap) {
  const fams = new Set(d.layers.flatMap((l) => (l.type === 'text' || l.type === 'button') && l.content.fontAssetId ? [l.content.fontFamily] : []));
  const direct = new Set(d.layers.flatMap((l) => (l.type === 'text' || l.type === 'button') && l.content.fontAssetId ? [l.content.fontAssetId] : []));
  d.assetIds = d.assetIds.filter((id) => assets[id]?.kind !== 'font' || direct.has(id) || fams.has(assets[id]?.family ?? ''));
}

function PropsPanel({ doc, layer, kit, assets, library, campaignCopy, onChange }: Props) {
  if (!layer) {
    return (
      <section className="ed-panel" aria-labelledby="props-h">
        <h3 id="props-h" className="ed-h">באנר <span className="mono muted">{doc.width}×{doc.height}</span></h3>
        <ColorField label="צבע רקע" value={doc.background.color} kit={kit} onChange={(c) => onChange(updateDoc(doc, (d) => { d.background.color = c; }), 'bg')} />
        <p className="small muted">בחרו שכבה בקנבס או ברשימת השכבות כדי לערוך אותה. מידות הבאנר קבועות לפי המיקום.</p>
      </section>
    );
  }
  const l = layer;
  const set = (key: string, fn: (x: Layer) => void) => onChange(updateLayer(doc, l.id, fn), `${key}-${l.id}`);
  const fams = [...new Set(kit.fonts.map((f) => f.family))];
  return (
    <section className="ed-panel" aria-labelledby="props-h">
      <h3 id="props-h" className="ed-h">{l.name} <span className="muted small">{TYPE_LABEL[l.type]}{l.role ? ` · ${ROLE_LABEL[l.role]}` : ''}</span></h3>
      {l.locked && <div className="alert info small">השכבה נעולה לגרירה בקנבס. עריכת מאפיינים עדיין אפשרית.</div>}
      <fieldset className="pgroup">
        <legend>מיקום וגודל</legend>
        <div className="pgrid">
          <Num label="X" value={l.x} onChange={(n) => set('x', (x) => { x.x = n; })} />
          <Num label="Y" value={l.y} onChange={(n) => set('y', (x) => { x.y = n; })} />
          <Num label="רוחב" value={l.width} min={1} onChange={(n) => set('w', (x) => { x.width = n; })} />
          <Num label="גובה" value={l.height} min={1} onChange={(n) => set('h', (x) => { x.height = n; })} />
          <Num label="סיבוב" suffix="°" value={l.rotation} min={-360} max={360} onChange={(n) => set('rot', (x) => { x.rotation = n; })} />
          <Num label="שקיפות" value={l.opacity} step={0.05} min={0} max={1} onChange={(n) => set('op', (x) => { x.opacity = n; })} />
          <Num label="z" value={l.zIndex} min={0} max={999} onChange={(n) => set('z', (x) => { x.zIndex = Math.round(n); })} />
        </div>
        <div className="pf">
          <span id="align-l">יישור ביחס לבאנר</span>
          <div className="seg" role="group" aria-labelledby="align-l">
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.x = 0; }))} title="שמאל">⇤ שמאל</button>
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.x = Math.round((doc.width - x.width) / 2); }))} title="מרכז אופקי">↔ מרכז</button>
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.x = doc.width - x.width; }))} title="ימין">ימין ⇥</button>
          </div>
          <div className="seg" role="group" aria-labelledby="align-l">
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.y = 0; }))}>⤒ למעלה</button>
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.y = Math.round((doc.height - x.height) / 2); }))}>↕ אמצע</button>
            <button type="button" onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.y = doc.height - x.height; }))}>⤓ למטה</button>
          </div>
        </div>
      </fieldset>

      {(l.type === 'text' || l.type === 'button') && (() => {
        const c = l.content;
        const approved = approvedCopy(campaignCopy, c.copyKey);
        const weights = [...new Set([...kit.fonts.filter((f) => f.family === c.fontFamily).flatMap((f) => f.weights), c.fontWeight])].sort((a, b) => a - b);
        const setFont = (family: string | null, weight: number) => onChange(updateDoc(doc, (d) => {
          const t = d.layers.find((x) => x.id === l.id)!;
          if (t.type !== 'text' && t.type !== 'button') return;
          t.content.fontWeight = weight;
          if (family === null) { t.content.fontAssetId = null; pruneFonts(d, assets); return; }
          t.content.fontFamily = family;
          const embeddable = kit.fonts.filter((f) => f.family === family && f.assetId && f.embedLicensed);
          const exact = embeddable.find((f) => f.weights.includes(weight)) ?? embeddable[0];
          t.content.fontAssetId = exact?.assetId ?? null;
          for (const f of embeddable) if (f.assetId && !d.assetIds.includes(f.assetId)) d.assetIds.push(f.assetId);
          pruneFonts(d, assets);
        }));
        return (
          <fieldset className="pgroup">
            <legend>טקסט</legend>
            {c.locked && (
              <div className="alert warn small" role="note">
                🔒 טקסט קבוע בקמפיין{c.copyKey ? <> (<span className="mono">{c.copyKey}</span>)</> : null}. ה-AI לא יכול לערוך אותו. עריכה ידנית מותרת, אבל תיצור סטייה מהנוסח המאושר.
              </div>
            )}
            <label className="pf"><span>תוכן</span>
              <textarea value={c.text} dir={c.direction === 'auto' ? 'auto' : c.direction} maxLength={500} rows={3} onChange={(e) => set('text', (x) => { if (x.type === 'text' || x.type === 'button') x.content.text = e.target.value; })} />
            </label>
            {approved !== null && approved !== c.text && (
              <div className="small" style={{ color: 'var(--warn)' }}>
                שונה מהנוסח המאושר בקמפיין. <button className="linkish" type="button" onClick={() => set('text', (x) => { if (x.type === 'text' || x.type === 'button') x.content.text = approved; })}>שחזור הנוסח המאושר</button>
              </div>
            )}
            <label className="pf"><span>פונט</span>
              <select value={c.fontAssetId ? c.fontFamily : '__system'} onChange={(e) => setFont(e.target.value === '__system' ? null : e.target.value, c.fontWeight)}>
                {fams.map((f) => <option key={f} value={f}>{f}{kit.fonts.some((k) => k.family === f && k.assetId && k.embedLicensed) ? '' : ' (ללא קובץ מורשה)'}</option>)}
                {!fams.includes(c.fontFamily) && c.fontAssetId && <option value={c.fontFamily}>{c.fontFamily}</option>}
                <option value="__system">פונט מערכת — טיוטה בלבד</option>
              </select>
            </label>
            <div className="pgrid">
              <label className="pf"><span>משקל</span>
                <select value={c.fontWeight} onChange={(e) => setFont(c.fontAssetId ? c.fontFamily : null, Number(e.target.value))}>
                  {weights.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
              <Num label="גודל" suffix="px" value={c.fontSize} min={4} max={400} step={0.5} onChange={(n) => set('fs', (x) => { if (x.type === 'text' || x.type === 'button') x.content.fontSize = n; })} />
              <Num label="גובה שורה" value={c.lineHeight} min={0.5} max={4} step={0.05} onChange={(n) => set('lh', (x) => { if (x.type === 'text' || x.type === 'button') x.content.lineHeight = n; })} />
              <Num label="ריווח אותיות" suffix="px" value={c.letterSpacing} min={-10} max={50} step={0.1} onChange={(n) => set('ls', (x) => { if (x.type === 'text' || x.type === 'button') x.content.letterSpacing = n; })} />
            </div>
            {c.fontSize < kit.minFontSizePx && <div className="small" style={{ color: 'var(--warn)' }}>קטן מגודל המינימום של המותג ({kit.minFontSizePx}px)</div>}
            <Seg label="יישור" value={c.align} options={[['start', 'התחלה'], ['center', 'מרכז'], ['end', 'סוף']]} onChange={(v) => set('al', (x) => { if (x.type === 'text' || x.type === 'button') x.content.align = v; })} />
            <Seg label="יישור אנכי" value={c.verticalAlign} options={[['top', 'למעלה'], ['middle', 'אמצע'], ['bottom', 'למטה']]} onChange={(v) => set('va', (x) => { if (x.type === 'text' || x.type === 'button') x.content.verticalAlign = v; })} />
            <Seg label="כיוון" value={c.direction} options={[['rtl', 'RTL'], ['ltr', 'LTR'], ['auto', 'אוטומטי']]} onChange={(v) => set('dir', (x) => { if (x.type === 'text' || x.type === 'button') x.content.direction = v; })} />
            <ColorField label="צבע טקסט" value={c.color} kit={kit} onChange={(hex) => set('color', (x) => { if (x.type === 'text' || x.type === 'button') x.content.color = hex; })} />
          </fieldset>
        );
      })()}

      {l.type === 'button' && (
        <fieldset className="pgroup">
          <legend>כפתור</legend>
          <ColorField label="מילוי" value={l.content.fill} kit={kit} onChange={(hex) => set('fill', (x) => { if (x.type === 'button') x.content.fill = hex; })} />
          <Num label="רדיוס פינות" suffix="px" value={l.content.radius} min={0} max={1000} onChange={(n) => set('rad', (x) => { if (x.type === 'button') x.content.radius = n; })} />
        </fieldset>
      )}

      {l.type === 'shape' && (
        <fieldset className="pgroup">
          <legend>צורה</legend>
          <ColorField label="מילוי" value={l.content.fill} kit={kit} onChange={(hex) => set('fill', (x) => { if (x.type === 'shape') x.content.fill = hex; })} />
          <Num label="רדיוס פינות" suffix="px" value={l.content.radius} min={0} max={1000} onChange={(n) => set('rad', (x) => { if (x.type === 'shape') x.content.radius = n; })} />
        </fieldset>
      )}

      {l.type === 'image' && (() => {
        const c = l.content;
        const a = assets[c.assetId];
        const imgs = library.filter((x) => ['image', 'logo', 'background', 'icon'].includes(x.kind));
        return (
          <fieldset className="pgroup">
            <legend>תמונה</legend>
            <label className="pf"><span>נכס</span>
              <select value={c.assetId} onChange={(e) => {
                const id = e.target.value;
                onChange(updateDoc(doc, (d) => {
                  const t = d.layers.find((x) => x.id === l.id)!;
                  if (t.type !== 'image') return;
                  const old = t.content.assetId;
                  t.content.assetId = id;
                  if (!d.assetIds.includes(id)) d.assetIds.push(id);
                  const stillUsed = d.layers.some((x) => x.type === 'image' && x.content.assetId === old) || d.background.assetId === old;
                  if (!stillUsed) d.assetIds = d.assetIds.filter((x) => x !== old);
                }));
              }}>
                {!imgs.some((x) => x.id === c.assetId) && <option value={c.assetId}>{a?.name ?? c.assetId}</option>}
                {imgs.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.kind}</option>)}
              </select>
            </label>
            {l.role === 'logo' && <div className="small muted">לוגו אינו נוצר או משתנה ב-AI. אפשר להחליף רק בנכס מאושר מהספרייה.</div>}
            <Seg label="התאמה" value={c.fit} options={[['contain', 'contain — הכל נראה'], ['cover', 'cover — חיתוך']]} onChange={(v) => set('fit', (x) => { if (x.type === 'image') x.content.fit = v; })} />
            <div className="pf">
              <span>נקודת מוקד לחיתוך — לחצו על התמונה</span>
              {a ? (
                <button type="button" className="focal-pick" aria-label="בחירת נקודת מוקד: לחיצה על נקודה בתמונה"
                  onClick={(e) => {
                    const r = (e.currentTarget.querySelector('img') as HTMLImageElement).getBoundingClientRect();
                    const fx = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
                    const fy = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
                    onChange(updateLayer(doc, l.id, (x) => { if (x.type === 'image') { x.content.focalX = Math.round(fx * 100) / 100; x.content.focalY = Math.round(fy * 100) / 100; } }));
                  }}>
                  <img src={a.url} alt="" />
                  <span className="focal-dot" style={{ left: `${c.focalX * 100}%`, top: `${c.focalY * 100}%` }} />
                </button>
              ) : <span className="small" style={{ color: 'var(--danger)' }}>הנכס לא נטען</span>}
            </div>
            <div className="pgrid">
              <Num label="מוקד X" value={c.focalX} min={0} max={1} step={0.05} onChange={(n) => set('fx', (x) => { if (x.type === 'image') x.content.focalX = n; })} />
              <Num label="מוקד Y" value={c.focalY} min={0} max={1} step={0.05} onChange={(n) => set('fy', (x) => { if (x.type === 'image') x.content.focalY = n; })} />
            </div>
          </fieldset>
        );
      })()}
    </section>
  );
}
export default memo(PropsPanel);
